import { fold, isRight } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/pipeable";
import type * as io from "io-ts";

// https://stackoverflow.com/a/11231664/264514
const getTypeCache: { [k: string]: string } = {};

const getType = (obj: unknown) => {
  let key;
  return obj === null
    ? "null" // null
    : obj === global
      ? "global" // window in browser or global in nodejs
      : (key = typeof obj) === "object"
        ? typeof obj === "object" && "nodeType" in obj
          ? "object" // DOM element
          : getTypeCache[(key = {}.toString.call(obj))] ||
            (getTypeCache[key] = key.slice(8, -1).toLowerCase()) // basic: string, boolean, number, function
        : key; // cached. date, regexp, error, object, array, math // get XXXX from [object XXXX], and cache it
};

interface ErrorPath {
  path: string;
  expectedType: string;
  foundType: string;
  foundValue: unknown;
}

// https://github.com/gcanti/io-ts#error-reporters
const getPaths = <A>(v: io.Validation<A>): ErrorPath[] =>
  pipe(
    v,
    fold(
      errors =>
        errors.map(error => {
          const path = error.context.map(({ key }) => key).join("/");
          const expectedType = error.context[error.context.length - 1]!.type.name;
          const foundType = getType(error.value);
          const foundValue = error.value;

          return {
            path,
            expectedType,
            foundType,
            foundValue,
          };
        }),
      () => [],
    ),
  );

export class ValidationError extends Error {
  errors: ErrorPath[];

  constructor(errors: ErrorPath[]) {
    super("Validation error");
    this.errors = errors;
  }

  toString() {
    return JSON.stringify(this.errors, null, 2);
  }
}

function validateInterface<T, D>(ioInterface: io.Type<T, D>, data: unknown) {
  if (process.env["NODE_ENV"] === "development") {
    const validatedResult = ioInterface.decode(data);

    if (isRight(validatedResult)) {
      return validatedResult.right;
    }

    const errors = getPaths(validatedResult);
    // eslint-disable-next-line no-console
    console.error("validation error", errors);
    throw new ValidationError(errors);
  } else return data as T;
}

export default validateInterface;
